/**
 * Authenticated calls, and the single-flight refresh that keeps a 15-minute access token from
 * turning into a stampede.
 *
 * The access token expires every 15 minutes (arch-08 §5). A console screen typically has several
 * queries in the air at once, so expiry does not produce one 401 — it produces N, simultaneously.
 * Handled naively that is N refresh calls racing, and because **admin refresh tokens are
 * single-use and rotating**, the losers of that race present a token that has already been spent.
 * The backend reads a spent refresh token as theft and kills the whole session family (R10). So
 * the naive version does not merely waste calls: it signs the operator out, at random, under load.
 *
 * Hence: **exactly one refresh in flight.** Concurrent 401s all await the same promise, then each
 * retries its own request once with the new token. A 401 on that retry is a hard logout — the
 * token is fresh, so the answer is about authorisation, not staleness, and retrying again would
 * loop.
 *
 * Client RBAC is UX only; none of this is a security boundary. It is the plumbing that makes the
 * server's boundary bearable to sit behind.
 */
import { apiFetch, ApiError, type ApiRequest } from './client';

export interface TokenPair {
  access: string;
  refresh: string;
}

/**
 * Where the tokens live. Split out so the refresh logic can be tested without a browser, and so
 * `lib/auth/` can own the storage decision (access in memory, refresh in `sessionStorage`) without
 * this file knowing about either.
 */
export interface TokenStore {
  read(): TokenPair | null;
  write(pair: TokenPair): void;
  clear(): void;
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
}

export interface AuthedClientDeps {
  tokens: TokenStore;
  /** Called once when the session is definitively over. Clearing has already happened. */
  onSignedOut: (reason: SignOutReason) => void;
}

/** Why the session ended — the shell shows a different thing for each. */
export type SignOutReason = 'expired' | 'revoked' | 'unauthenticated';

export const NO_SESSION = new ApiError({
  status: 401,
  code: 'no_session',
  detail: 'You are signed out.',
});

export interface AuthedClient {
  request<T>(path: string, init?: ApiRequest): Promise<T>;
  /** Forces the refresh path. Exposed for the session bootstrap, which has no request to make. */
  refresh(): Promise<TokenPair | null>;
}

export function createAuthedClient({ tokens, onSignedOut }: AuthedClientDeps): AuthedClient {
  /* The whole point of this module lives in this one variable. */
  let inFlight: Promise<TokenPair | null> | null = null;

  function endSession(reason: SignOutReason): null {
    tokens.clear();
    onSignedOut(reason);
    return null;
  }

  async function doRefresh(refreshToken: string): Promise<TokenPair | null> {
    try {
      const body = await apiFetch<RefreshResponse>('/v1/auth/refresh', {
        method: 'POST',
        body: { refresh_token: refreshToken },
      });
      const pair: TokenPair = { access: body.access_token, refresh: body.refresh_token };
      /* Rotation: the new refresh token replaces the spent one immediately. A window where the
         old one is still stored is a window where a reload presents a dead token. */
      tokens.write(pair);
      return pair;
    } catch (error) {
      /* **Only the server refusing the refresh token ends the session.** A 500, a timeout or a
         dropped wifi connection is not a revocation, and signing someone out of a console
         mid-shift because their network blinked is its own bug. */
      return error instanceof ApiError && error.isAuth ? endSession('expired') : null;
    }
  }

  function refresh(): Promise<TokenPair | null> {
    if (inFlight) return inFlight;
    const current = tokens.read();
    if (!current) return Promise.resolve(endSession('unauthenticated'));
    inFlight = doRefresh(current.refresh).finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  async function request<T>(path: string, init: ApiRequest = {}): Promise<T> {
    const current = tokens.read();
    if (!current) {
      endSession('unauthenticated');
      throw NO_SESSION;
    }

    const withBearer = (token: string): ApiRequest => ({
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${token}` },
    });

    try {
      return await apiFetch<T>(path, withBearer(current.access));
    } catch (error) {
      if (!(error instanceof ApiError) || !error.isAuth) throw error;

      const renewed = await refresh();
      if (!renewed) throw error;

      try {
        return await apiFetch<T>(path, withBearer(renewed.access));
      } catch (retryError) {
        /* A 401 on a token minted seconds ago is not staleness. Either the session was revoked
           mid-flight — a scope change does exactly that (backend 07 §5.1) — or this admin may
           genuinely not call this endpoint. Retrying again would spin. */
        if (retryError instanceof ApiError && retryError.isAuth) {
          endSession('revoked');
        }
        throw retryError;
      }
    }
  }

  return { request, refresh };
}
